import React from "react";
import { listSheltersByIds } from "../../data/shelters";
import { logAudit } from "../../utils/auditLog";
import { ROLES } from "../../utils/enums";
import { applyActiveShelterContext, resolveActiveShelterContext } from "../../utils/sessionUser";
import { getSupabaseClient } from "../../utils/supabase";

export function PostLoginSelection({ user, onComplete }) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const needsRoleSelection = user.role === ROLES.COMBINED;
  const needsShelterSelection = user.shelterCodes && user.shelterCodes.length > 1;
  const needsSelection = needsRoleSelection || needsShelterSelection;

  const [selectedMode, setSelectedMode] = React.useState("");
  const [selectedShelter, setSelectedShelter] = React.useState("");
  const [availableShelters, setAvailableShelters] = React.useState([]);

  React.useEffect(function () {
    if (needsShelterSelection) {
      setLoading(true);
      // Identity Phase 2: fetch the chooser rows by the membership surrogate ids.
      // Rows still carry shelter_code (SHELTER_PICKER_COLS), so the <option> value
      // + selection logic below is unchanged — only the fetch source moved.
      listSheltersByIds(user.shelterIds).then(function (rows) {
        if (rows) setAvailableShelters(rows);
        else setError("Failed to load DFV Services. Please refresh and try again.");
        setLoading(false);
      });
    }
  }, []);

  // Intentional fire-once-on-mount: if the user doesn't need to make a
  // selection, skip straight past this screen. Empty deps are deliberate —
  // user/onComplete don't change while this component is mounted (parent
  // re-mounts on login/logout), and the manual selection flow goes through
  // handleComplete, not this effect.
  React.useEffect(function () {
    if (!needsSelection) {
      onComplete(user);
    }
  }, []);

  async function handleComplete() {
    if (needsRoleSelection && !selectedMode) {
      setError("Please select your role for this session.");
      return;
    }
    if (needsShelterSelection && !selectedShelter) {
      setError("Please select which DFV Service you are working for today.");
      return;
    }

    setLoading(true);
    setError("");

    const updatedUser = Object.assign({}, user);

    if (needsRoleSelection) {
      updatedUser.sessionMode = selectedMode;
    }

    if (needsShelterSelection) {
      const shelter = availableShelters.find(function (s) {
        return s.shelter_code === selectedShelter;
      });
      // The SELECTED membership (its shelter_id + contact phone/mobile), carried on
      // the user from login/restore.
      const membership = (user.shelterMemberships || []).find(function (m) {
        return m.shelter_code === selectedShelter;
      });
      const selectedId = shelter?.id || membership?.shelter_id || null;
      // Rebuild the ENTIRE per-shelter context from the selected shelter via the same
      // helper login/restore use — so EVERY shelter-dependent field (state, address,
      // delivery addresses, contact prefill, name, code, uuid) follows the selection.
      // This is what fixes the header showing the primary shelter's state (e.g. QLD)
      // after picking a different-state (NSW) shelter: nothing is left un-overwritten.
      const ctx = await resolveActiveShelterContext(
        selectedId,
        membership || null,
        user.role === ROLES.SHELTER
      );
      // Apply the FULL resolved context in one shot (the SAME applier login uses) so
      // no shelter-dependent field can be silently dropped here — every field of
      // ActiveShelterContext, including firstName/lastName, follows the selection.
      // This replaces the hand-copied list that missed firstName/lastName (the 6 Aug
      // "Contact Name shows the DFV Service name" bug) and, before it, state (1 Aug).
      applyActiveShelterContext(updatedUser, ctx);
      // Chooser-specific values the context can't express, applied AFTER so they win:
      // activeShelterCode isn't a context field; shelterCode is the picked code (ctx's
      // is "" when the membership row is absent); shelterName/shelterUuid keep their
      // display fallbacks off the fetched chooser row.
      updatedUser.activeShelterCode = selectedShelter;
      updatedUser.shelterCode = selectedShelter;
      // A shelter caseworker who picked a shelter → attribute to THAT org name. An internal user
      // (a combined user picking a mode) has no shelter, so fall back to the name login already
      // resolved via attributedName() — their "Display Name" (preferred_name) — NOT the raw code.
      // (applyActiveShelterContext above clobbered shelterName to ctx's "" for the no-shelter case.)
      updatedUser.shelterName =
        ctx.shelterName || (shelter ? shelter.shelter_name : user.shelterName || selectedShelter);
      updatedUser.shelterUuid = ctx.shelterUuid || shelter?.id || null;
    }

    setLoading(false);
    onComplete(updatedUser);
  }

  if (!needsSelection) return null;
  if (loading && availableShelters.length === 0) {
    return React.createElement(
      "div",
      {
        style: {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
        },
      },
      React.createElement(
        "div",
        {
          style: {
            background: "white",
            padding: 32,
            borderRadius: 8,
            minWidth: 400,
            textAlign: "center",
          },
        },
        React.createElement("div", null, "Loading...")
      )
    );
  }

  return React.createElement(
    "div",
    {
      style: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
      },
    },
    React.createElement(
      "div",
      {
        style: { background: "white", padding: 32, borderRadius: 8, minWidth: 400, maxWidth: 500 },
      },
      React.createElement(
        "h2",
        { style: { margin: "0 0 24px", fontSize: 20, color: "#00695C" } },
        "Welcome Back!"
      ),

      needsRoleSelection &&
        React.createElement(
          "div",
          { style: { marginBottom: 24 } },
          React.createElement(
            "div",
            { style: { fontSize: 14, fontWeight: 600, marginBottom: 12, color: "#1A2E2B" } },
            "What are you doing today?"
          ),
          React.createElement(
            "div",
            { style: { display: "flex", gap: 12, flexDirection: "column" } },
            React.createElement(
              "label",
              {
                style: {
                  display: "flex",
                  alignItems: "center",
                  padding: 12,
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  cursor: "pointer",
                  background: selectedMode === "responder" ? "#E3F2FD" : "white",
                },
              },
              React.createElement("input", {
                type: "radio",
                name: "sessionMode",
                value: "responder",
                checked: selectedMode === "responder",
                onChange: function (e) {
                  setSelectedMode(e.target.value);
                },
                style: { marginRight: 8 },
              }),
              React.createElement(
                "div",
                null,
                React.createElement("div", { style: { fontWeight: 600 } }, "Responder"),
                React.createElement(
                  "div",
                  { style: { fontSize: 12, color: "#607D79" } },
                  "Review and approve requests"
                )
              )
            ),
            React.createElement(
              "label",
              {
                style: {
                  display: "flex",
                  alignItems: "center",
                  padding: 12,
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  cursor: "pointer",
                  background: selectedMode === ROLES.PICKER ? "#E3F2FD" : "white",
                },
              },
              React.createElement("input", {
                type: "radio",
                name: "sessionMode",
                value: "picker",
                checked: selectedMode === ROLES.PICKER,
                onChange: function (e) {
                  setSelectedMode(e.target.value);
                },
                style: { marginRight: 8 },
              }),
              React.createElement(
                "div",
                null,
                React.createElement("div", { style: { fontWeight: 600 } }, "Picker"),
                React.createElement(
                  "div",
                  { style: { fontSize: 12, color: "#607D79" } },
                  "Fulfill and dispatch orders"
                )
              )
            )
          )
        ),

      needsShelterSelection &&
        React.createElement(
          "div",
          { style: { marginBottom: 24 } },
          React.createElement(
            "div",
            { style: { fontSize: 14, fontWeight: 600, marginBottom: 12, color: "#1A2E2B" } },
            "Which DFV Service are you working for today?"
          ),
          React.createElement(
            "select",
            {
              value: selectedShelter,
              onChange: function (e) {
                setSelectedShelter(e.target.value);
              },
              style: {
                width: "100%",
                padding: 12,
                border: "1px solid #ddd",
                borderRadius: 6,
                fontSize: 14,
              },
            },
            React.createElement("option", { value: "" }, "Select DFV Service..."),
            availableShelters.map(function (shelter) {
              return React.createElement(
                "option",
                { key: shelter.shelter_code, value: shelter.shelter_code },
                `${shelter.shelter_name} (${shelter.shelter_code})`
              );
            })
          )
        ),

      error &&
        React.createElement(
          "div",
          { style: { color: "#d32f2f", fontSize: 12, marginBottom: 16, textAlign: "center" } },
          error
        ),

      React.createElement(
        "div",
        { style: { textAlign: "center" } },
        React.createElement(
          "button",
          {
            onClick: handleComplete,
            disabled: loading,
            style: {
              background: "#00695C",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              opacity: loading ? 0.7 : 1,
            },
          },
          loading ? "Starting..." : "Continue"
        ),
        React.createElement(
          "div",
          { style: { marginTop: 16 } },
          React.createElement(
            "button",
            {
              onClick: function () {
                logAudit("logout", {
                  user_id: user?.id || "",
                  email: user?.email || "",
                  from: "post_login_selection",
                });
                getSupabaseClient()
                  .auth.signOut()
                  .then(function () {
                    window.location.reload();
                  });
              },
              style: {
                background: "none",
                border: "none",
                color: "#607D79",
                fontSize: 12,
                cursor: "pointer",
                textDecoration: "underline",
                fontFamily: "inherit",
              },
            },
            "Sign out and use a different account"
          )
        )
      )
    )
  );
}
